/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import {
  TableContainer,
  Paper,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Tooltip,
} from "@mui/material";

export default function AnalyticsTable({
  weekDays,
  analytics,
  salesProjectionsInput,
  handleSalesProjectionChange,
  handleSalesProjectionBlur,
  byDept,
  deptNameMap,
  showTopRow = true,
  colWidths = null,
  totalsColWidth = 96,
  namesColWidth = 160,
  forecastedSalesInput = {},
  handleForecastedSalesChange = () => {},
  handleForecastedSalesBlur = () => {},
  actualSalesInput = {},
  handleActualSalesChange = () => {},
  handleActualSalesBlur = () => {},
}) {
  // sticky left column styles. Use separate header vs body top so body cells do not overlap header rows.
  const baseLeft = {
    position: "sticky",
    left: 0,
    minWidth: Math.max(48, Math.floor(namesColWidth)),
    boxShadow: "2px 0 4px rgba(0,0,0,0.03)",
    paddingRight: 6,
    paddingLeft: 8,
    textAlign: "left",
  };
  const leftHeaderSx = { ...baseLeft, zIndex: 2200, top: 0 };

  // dynamically measure header height and use it for body left cells so we don't hardcode pixel values
  const headerRef = useRef(null);
  const [headerTop, setHeaderTop] = useState(0);
  useLayoutEffect(() => {
    function measure() {
      const h = headerRef.current?.getBoundingClientRect()?.height || 0;
      // add a small gap so body cells sit comfortably below header
      setHeaderTop(Math.ceil(h) + 2);
    }
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [weekDays, colWidths]);

  const leftBodySx = {
    ...baseLeft,
    zIndex: 1200,
    top: `${headerTop}px`,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    textAlign: "left",
    width: Math.floor(namesColWidth),
    maxWidth: Math.floor(namesColWidth),
    boxSizing: "border-box",
    overflow: "hidden",
    flexShrink: 0,
    backgroundColor: "#F5EEE3",
  };
  const labelMaxWidth = Math.max(100, Math.floor(namesColWidth) - 32);

  // palette
  const panelBg = "#F7F1E3";
  const headerBg = "#F0E2C7"; // manila header
  const rowBg = "#FFFCF5";
  const colAltBg = "#F5EEE3";
  const rowAltBg = rowBg;

  // Load Inter from Google Fonts
  useEffect(() => {
    if (typeof document === "undefined") return;
    const id = "gwf-inter-font";
    if (document.getElementById(id)) return;
    const l = document.createElement("link");
    l.id = id;
    l.rel = "stylesheet";
    l.href =
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap";
    document.head.appendChild(l);
  }, []);

  const interFamily =
    "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial";

  // Typography styles
  const headerStyle = {
    fontFamily: interFamily,
    fontWeight: 600,
  whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
  maxWidth: labelMaxWidth,
  fontSize: 13,
  lineHeight: '20px',
  };
  const primaryValueStyle = {
    fontFamily: interFamily,
    fontWeight: 500,
    fontSize: 12,
    color: "#5B2A86",
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum" 1',
  };
  const secondaryValueStyle = {
    fontFamily: interFamily,
    fontWeight: 400,
    fontSize: 12,
    color: "#5B2A86",
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum" 1',
  };
  const numericCellSx = {
    fontFamily: interFamily,
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum" 1',
    fontWeight: 400,
    textAlign: "center",
  };

  // visibility toggle state: 'forecast' | 'actual' | 'both'
  const [salesView, setSalesView] = useState("both");
  const handleSalesViewChange = (e, v) => {
    if (v) setSalesView(v);
  };

  const toLocalDateKey = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // derive weekly totals from current input maps so UI updates immediately when user edits per-day inputs
  const derivedWeekTotals = React.useMemo(() => {
    const sumFromMap = (map) => {
      if (!map || typeof map !== "object") return 0;
      return weekDays.reduce((acc, d) => {
        const key = toLocalDateKey(d);
        const v = map[key];
        const n =
          typeof v === "number"
            ? v
            : v && !isNaN(Number(v))
            ? Number(v)
            : 0;
        return acc + n;
      }, 0);
    };
    const hasAnyKeyForWeek = (map) => {
      if (!map || typeof map !== "object") return false;
      return weekDays.some((d) => {
        const key = toLocalDateKey(d);
        return typeof map[key] !== "undefined" && map[key] !== null && map[key] !== "";
      });
    };
    const forecastSum = sumFromMap(forecastedSalesInput);
    const actualSum = sumFromMap(actualSalesInput);
    // If the user has provided any per-day values for the week (even zero),
    // prefer the input map totals so the UI mirrors edits immediately.
    const weekForecast = hasAnyKeyForWeek(forecastedSalesInput)
      ? forecastSum
      : analytics?.weekTotals && typeof analytics?.weekTotals?.forecastedSales === "number"
      ? analytics.weekTotals.forecastedSales
      : 0;
    const weekActual = hasAnyKeyForWeek(actualSalesInput)
      ? actualSum
      : analytics?.weekTotals && typeof analytics?.weekTotals?.actualSales === "number"
      ? analytics.weekTotals.actualSales
      : 0;
    return { forecastedSales: weekForecast, actualSales: weekActual };
  // include stringified snapshots of the input maps so changes to their values
  // (not just object identity) trigger recalculation
  }, [
    weekDays,
    analytics?.weekTotals,
    JSON.stringify(forecastedSalesInput || {}),
    JSON.stringify(actualSalesInput || {}),
  ]);

  return (
  <Box sx={{ mt: 0, position: 'relative', zIndex: 10 }}>
    {/* Manila header with internal tab */}
    <Box
      sx={{
        position: 'relative',
        borderRadius: '12px 12px 0 0',
        border: '1px solid rgba(0,0,0,0.06)',
        borderBottom: 'none',
         bgcolor: colAltBg,
        pt: 3,          // space so the tab can sit “inside”
        pb: 1,
      }}
    >
      {/* Tab that lives fully inside the header (so it won't be clipped) */}
      <Box
        sx={{
          position: 'absolute',
          left: 32,
          top: 4,       // small offset from top edge
          borderRadius: '10px 10px 0 0',
          bgcolor: headerBg,
          px: 1.5,
          py: 0.5,
          boxShadow: '0 4px 10px rgba(16,24,40,0.12)',
          border: '1px solid rgba(0,0,0,0.06)',
          borderBottom: 'none',
        }}
      >
        <ToggleButtonGroup
          value={salesView}
          exclusive
          onChange={handleSalesViewChange}
          size="small"
          aria-label="sales view"
          sx={{ borderRadius: 16, boxShadow: 'none', bgcolor: 'transparent' }}
        >
          <ToggleButton
            value="forecast"
            aria-label="forecast"
            sx={{
              borderRadius: '9999px',
              textTransform: 'none',
              border: 'none',
              px: 1.2,
              '&.Mui-selected': {
                backgroundColor: '#5B2A86',
                color: '#fff',
              },
            }}
          >
            Forecast
          </ToggleButton>
          <ToggleButton
            value="actual"
            aria-label="actual"
            sx={{
              borderRadius: '9999px',
              textTransform: 'none',
              border: 'none',
              px: 1.2,
              '&.Mui-selected': {
                backgroundColor: '#5B2A86',
                color: '#fff',
              },
            }}
          >
            Actual
          </ToggleButton>
          <ToggleButton
            value="both"
            aria-label="both"
            sx={{
              borderRadius: '9999px',
              textTransform: 'none',
              border: 'none',
              px: 1.2,
              '&.Mui-selected': {
                backgroundColor: '#5B2A86',
                color: '#fff',
              },
            }}
          >
            Both
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Centered header label */}
      <Box
        sx={{
          fontFamily: interFamily,
          fontWeight: 600,
          textAlign: 'center',
          color: '#4B5563',
        }}
      >
        Weekly performance
      </Box>
    </Box>

    {/* Analytics card under the header */}
    <TableContainer
      component={Paper}
      sx={{
        mt: 0,
        overflowX: 'auto',
        position: 'relative',
        bgcolor: panelBg,
        borderRadius: '0 0 12px 12px',
        border: '1px solid rgba(0,0,0,0.06)',
        borderTop: 'none',              // connects cleanly to header above
        p: 1,
        boxShadow: '0 6px 18px rgba(16,24,40,0.06)',
      }}
    >
        <Table size="small" sx={{ tableLayout: colWidths ? "fixed" : "auto" }}>
          {/* If colWidths provided, render a colgroup to fix column widths to match schedule */}
          {colWidths &&
            Array.isArray(colWidths) &&
            colWidths.length === weekDays.length && (
              <colgroup>
                <col style={{ width: `${namesColWidth}px` }} />
                {typeof totalsColWidth === "number" && totalsColWidth > 0 && (
                  <col style={{ width: `${totalsColWidth}px` }} />
                )}
                {colWidths.map((w, i) => (
                  <col key={`col-${i}`} style={{ width: `${w}px` }} />
                ))}
              </colgroup>
            )}

          <TableBody>
            {/* Date row aligned with schedule columns */}
            <TableRow ref={headerRef}>
                <TableCell
                  sx={{
                    ...leftHeaderSx,
                    borderBottom: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    backgroundColor: colAltBg,
                  }}
                />
              {weekDays.map((d, i) => {
                const key = toLocalDateKey(d);
                const label = d.toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "numeric",
                  day: "numeric",
                });
                return (
                  <TableCell
                    key={`date-${key}`}
                    align="center"
                    sx={{
                      fontSize: 12,
                      color: "#6B7280",
                      borderBottom: "none",
                      backgroundColor: i % 2 === 0 ? rowBg : colAltBg,
                    }}
                  >
                    {label}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Forecasted Sales row (editable) */}
            {(salesView === "forecast" || salesView === "both") && (
              <TableRow sx={{ "& td, & th": { color: "#111827" } }}>
                <TableCell
                  sx={{
                    ...leftBodySx,
                    backgroundColor: colAltBg,
                    color: "#111827",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div style={headerStyle}>Forecasted Sales</div>
                      <Tooltip
                        title={
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              Projected Sales
                            </div>
                            <div style={{ marginTop: 6 }}>
                              “Your expected sales for the day based on your
                              forecast.”
                            </div>
                            <div style={{ marginTop: 8, fontWeight: 600 }}>
                              How is this calculated?
                            </div>
                            <div>
                              You enter the projected sales amount for each day.
                            </div>
                          </div>
                        }
                      >
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: "#6B7280",
                            cursor: "default",
                          }}
                        >
                          ⓘ
                        </span>
                      </Tooltip>
                    </div>
                    <div style={primaryValueStyle}>
                      {typeof derivedWeekTotals.forecastedSales === "number"
                        ? derivedWeekTotals.forecastedSales.toLocaleString(
                            "en-US",
                            { style: "currency", currency: "USD" }
                          )
                        : "—"}
                    </div>
                  </div>
                </TableCell>
                {weekDays.map((d, i) => {
                  const key = toLocalDateKey(d);
                  const perDayForecast =
                    forecastedSalesInput &&
                    typeof forecastedSalesInput[key] !== "undefined" &&
                    forecastedSalesInput[key] !== ""
                      ? Number(forecastedSalesInput[key])
                      : salesProjectionsInput &&
                        typeof salesProjectionsInput[key] !== "undefined"
                      ? Number(salesProjectionsInput[key])
                      : 0;
                  const schedHours =
                    analytics.totalHours &&
                    typeof analytics.totalHours[key] !== "undefined"
                      ? Number(analytics.totalHours[key])
                      : 0;
                  const val =
                    schedHours > 0 ? perDayForecast / schedHours : 0;
                  return (
                    <TableCell
                      key={key}
                      align="center"
                      sx={{
                        backgroundColor: i % 2 === 0 ? rowBg : colAltBg,
                        ...numericCellSx,
                      }}
                    >
                      <TextField
                        type="text"
                        variant="standard"
                        value={forecastedSalesInput[key] ?? ""}
                        onChange={handleForecastedSalesChange(key)}
                        onBlur={handleForecastedSalesBlur(key)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleForecastedSalesBlur(key)(e);
                            try {
                              e.target.blur();
                            } catch (_) {}
                          }
                        }}
                        placeholder="000"
                        sx={{ minWidth: 80 }}
                        inputProps={{
                          inputMode: "decimal",
                          pattern: "^\\d{0,6}(\\.\\d{0,2})?$",
                          maxLength: 9,
                          style: { textAlign: "center" },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              $
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {/* Actual Sales row (editable) */}
            {(salesView === "actual" || salesView === "both") && (
              <TableRow sx={{ "& td, & th": { color: "#111827" } }}>
                <TableCell
                  sx={{
                    ...leftBodySx,
                    backgroundColor: colAltBg,
                    color: "#111827",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div style={headerStyle}>Actual Sales</div>
                      <Tooltip
                        title={
                          <div>
                            <div style={{ fontWeight: 600 }}>Actual Sales</div>
                            <div style={{ marginTop: 6 }}>
                              “The total sales your location actually made.”
                            </div>
                            <div style={{ marginTop: 8, fontWeight: 600 }}>
                              How is this calculated?
                            </div>
                            <div>
                              Pulled from actual sales data recorded for the
                              day.
                            </div>
                          </div>
                        }
                      >
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: "#6B7280",
                            cursor: "default",
                          }}
                        >
                          ⓘ
                        </span>
                      </Tooltip>
                    </div>
                    <div style={primaryValueStyle}>
                      {typeof derivedWeekTotals.actualSales === "number"
                        ? derivedWeekTotals.actualSales.toLocaleString(
                            "en-US",
                            { style: "currency", currency: "USD" }
                          )
                        : "—"}
                    </div>
                  </div>
                </TableCell>
                {weekDays.map((d, i) => {
                  const key = toLocalDateKey(d);
                  return (
                    <TableCell
                      key={key}
                      align="center"
                      sx={{
                        backgroundColor: i % 2 === 0 ? rowBg : colAltBg,
                        ...numericCellSx,
                      }}
                    >
                      <TextField
                        type="text"
                        variant="standard"
                        value={actualSalesInput[key] ?? ""}
                        onChange={handleActualSalesChange(key)}
                        onBlur={handleActualSalesBlur(key)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleActualSalesBlur(key)(e);
                            try {
                              e.target.blur();
                            } catch (_) {}
                          }
                        }}
                        placeholder="000"
                        sx={{ minWidth: 80 }}
                        inputProps={{
                          inputMode: "decimal",
                          pattern: "^\\d{0,6}(\\.\\d{0,2})?$",
                          maxLength: 9,
                          style: { textAlign: "center" },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              $
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {/* Total Hours */}
            {(salesView === "forecast" || salesView === "both") && (
              <TableRow sx={{ "& td, & th": { color: "#111827" } }}>
                <TableCell
                  sx={{
                    ...leftBodySx,
                    backgroundColor: colAltBg,
                    color: "#111827",
                    boxShadow: "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{ ...headerStyle, maxWidth: labelMaxWidth }}
                      >
                        Sched. Hours
                      </div>
                      <Tooltip
                        title={
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              Scheduled Labor Hours
                            </div>
                            <div style={{ marginTop: 6 }}>
                              “The total number of labor hours you scheduled for
                              the day.”
                            </div>
                            <div style={{ marginTop: 8, fontWeight: 600 }}>
                              How is this calculated?
                            </div>
                            <div>
                              All scheduled shift hours for the day are added
                              together.
                            </div>
                          </div>
                        }
                      >
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: "#6B7280",
                            cursor: "default",
                          }}
                        >
                          ⓘ
                        </span>
                      </Tooltip>
                    </div>
                    <div style={secondaryValueStyle}>
                      {analytics.weekTotals.hours.toFixed(2)}
                    </div>
                  </div>
                </TableCell>
                {weekDays.map((d, i) => {
                  const key = d.toISOString().split("T")[0];
                  return (
                    <TableCell
                      key={key}
                      align="center"
                      sx={{
                        backgroundColor: i % 2 === 0 ? rowBg : colAltBg,
                        ...numericCellSx,
                      }}
                    >
                      {analytics.totalHours[key]?.toFixed(2) ?? "0.00"}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {/* Actual Hours */}
            {(salesView === "actual" || salesView === "both") && (
              <TableRow sx={{ "& td, & th": { color: "#111827" } }}>
                <TableCell
                  sx={{
                    ...leftBodySx,
                    backgroundColor: colAltBg,
                    color: "#111827",
                    boxShadow: "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{ ...headerStyle, maxWidth: labelMaxWidth }}
                      >
                        Actual Hours
                      </div>
                      <Tooltip
                        title={
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              Actual Labor Hours
                            </div>
                            <div style={{ marginTop: 6 }}>
                              “The number of labor hours your team actually
                              worked.”
                            </div>
                            <div style={{ marginTop: 8, fontWeight: 600 }}>
                              How is this calculated?
                            </div>
                            <div>
                              All clocked-in hours for the day are added
                              together.
                            </div>
                          </div>
                        }
                      >
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: "#6B7280",
                            cursor: "default",
                          }}
                        >
                          ⓘ
                        </span>
                      </Tooltip>
                    </div>
                    <div style={secondaryValueStyle}>
                      {typeof analytics.weekTotals.actualHours === "number"
                        ? analytics.weekTotals.actualHours.toFixed(2)
                        : "0.00"}
                    </div>
                  </div>
                </TableCell>
                {weekDays.map((d, i) => {
                  const key = d.toISOString().split("T")[0];
                  return (
                    <TableCell
                      key={key}
                      align="center"
                      sx={{
                        backgroundColor: i % 2 === 0 ? rowBg : colAltBg,
                        ...numericCellSx,
                      }}
                    >
                      {analytics.actualHours?.[key]?.toFixed(2) ??
                        analytics.totalHours[key]?.toFixed(2) ??
                        "0.00"}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {/* Scheduled Labor $ */}
            {(salesView === "forecast" || salesView === "both") && (
              <TableRow sx={{ "& td, & th": { color: "#111827" } }}>
                <TableCell
                  sx={{
                    ...leftBodySx,
                    backgroundColor: colAltBg,
                    color: "#111827",
                    boxShadow: "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{ ...headerStyle, maxWidth: labelMaxWidth }}
                      >
                        Sched. Labor $
                      </div>
                      <Tooltip
                        title={
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              Scheduled Labor Cost
                            </div>
                            <div style={{ marginTop: 6 }}>
                              “How much you plan to spend on labor based on the
                              schedule.”
                            </div>
                            <div style={{ marginTop: 8, fontWeight: 600 }}>
                              How is this calculated?
                            </div>
                            <div>
                              Scheduled Hours × Each employee’s hourly rate.
                            </div>
                          </div>
                        }
                      >
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: "#6B7280",
                            cursor: "default",
                          }}
                        >
                          ⓘ
                        </span>
                      </Tooltip>
                    </div>
                    <div style={primaryValueStyle}>
                      {analytics.weekTotals.cost.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </div>
                  </div>
                </TableCell>
                {weekDays.map((d, i) => {
                  const key = d.toISOString().split("T")[0];
                  return (
                    <TableCell
                      key={key}
                      align="center"
                      sx={{
                        backgroundColor: i % 2 === 0 ? rowBg : colAltBg,
                        ...numericCellSx,
                      }}
                    >
                      {analytics.laborCost[key]?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {/* Actual Labor $ */}
            {(salesView === "actual" || salesView === "both") && (
              <TableRow sx={{ "& td, & th": { color: "#111827" } }}>
                <TableCell
                  sx={{
                    ...leftBodySx,
                    backgroundColor: colAltBg,
                    color: "#111827",
                    boxShadow: "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{ ...headerStyle, maxWidth: labelMaxWidth }}
                      >
                        Actual Labor $
                      </div>
                      <Tooltip
                        title={
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              Actual Labor Cost
                            </div>
                            <div style={{ marginTop: 6 }}>
                              “The real labor dollars your location spent after
                              the shift.”
                            </div>
                            <div style={{ marginTop: 8, fontWeight: 600 }}>
                              How is this calculated?
                            </div>
                            <div>
                              Actual Hours × Each employee’s hourly rate.
                            </div>
                          </div>
                        }
                      >
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: "#6B7280",
                            cursor: "default",
                          }}
                        >
                          ⓘ
                        </span>
                      </Tooltip>
                    </div>
                    <div style={primaryValueStyle}>
                      {typeof analytics.weekTotals.actualLabor === "number"
                        ? analytics.weekTotals.actualLabor.toLocaleString(
                            "en-US",
                            { style: "currency", currency: "USD" }
                          )
                        : "$0.00"}
                    </div>
                  </div>
                </TableCell>
                {weekDays.map((d, i) => {
                  const key = d.toISOString().split("T")[0];
                  return (
                    <TableCell
                      key={key}
                      align="center"
                      sx={{
                        backgroundColor: i % 2 === 0 ? rowBg : colAltBg,
                        ...numericCellSx,
                      }}
                    >
                      {analytics.actualLabor?.[key]?.toLocaleString
                        ? analytics.actualLabor[key].toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })
                        : analytics.laborCost[key]?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          }) ?? "$0.00"}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {/* Scheduled Labor % */}
            {(salesView === "forecast" || salesView === "both") && (
              <TableRow sx={{ "& td, & th": { color: "#111827" } }}>
                <TableCell
                  sx={{
                    ...leftBodySx,
                    color: "#111827",
                    boxShadow: "none",
                    backgroundColor: colAltBg,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{ ...headerStyle, maxWidth: labelMaxWidth }}
                      >
                        Sched. Labor %
                      </div>
                      <Tooltip
                        title={
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              Scheduled Labor Percentage
                            </div>
                            <div style={{ marginTop: 6 }}>
                              “Your scheduled labor cost compared to your
                              projected sales.”
                            </div>
                            <div style={{ marginTop: 8, fontWeight: 600 }}>
                              How is this calculated?
                            </div>
                            <div>
                              (Scheduled Labor Cost ÷ Projected Sales) × 100.
                            </div>
                          </div>
                        }
                      >
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: "#6B7280",
                            cursor: "default",
                          }}
                        >
                          ⓘ
                        </span>
                      </Tooltip>
                    </div>
                    <div style={primaryValueStyle}>
                      {(() => {
                        const weekCost = Number(
                          (analytics.weekTotals && analytics.weekTotals.cost) || 0
                        );
                        const weekForecast = Number(
                          (derivedWeekTotals && derivedWeekTotals.forecastedSales) || 0
                        );
                        return weekForecast > 0
                          ? ((weekCost / weekForecast) * 100).toFixed(2) + "%"
                          : "0.00%";
                      })()}
                    </div>
                  </div>
                </TableCell>
                {weekDays.map((d, i) => {
                  const key = d.toISOString().split("T")[0];
                  return (
                    <TableCell
                      key={key}
                      align="center"
                      sx={{
                        backgroundColor: i % 2 === 0 ? rowBg : colAltBg,
                        ...numericCellSx,
                      }}
                    >
                      {(() => {
                        const perDayForecast =
                          forecastedSalesInput &&
                          typeof forecastedSalesInput[key] !== "undefined" &&
                          forecastedSalesInput[key] !== ""
                            ? Number(forecastedSalesInput[key])
                            : salesProjectionsInput &&
                              typeof salesProjectionsInput[key] !== "undefined"
                            ? Number(salesProjectionsInput[key])
                            : 0;
                        const schedCost =
                          analytics.laborCost &&
                          typeof analytics.laborCost[key] !== "undefined"
                            ? Number(analytics.laborCost[key])
                            : 0;
                        const pct = perDayForecast > 0 ? (schedCost / perDayForecast) * 100 : 0;
                        return pct.toFixed(2);
                      })()}%
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {/* Actual Labor % */}
            {(salesView === "actual" || salesView === "both") && (
              <TableRow sx={{ "& td, & th": { color: "#111827" } }}>
                <TableCell
                  sx={{
                    ...leftBodySx,
                    color: "#111827",
                    boxShadow: "none",
                    backgroundColor: colAltBg,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{ ...headerStyle, maxWidth: labelMaxWidth }}
                      >
                        Actual Labor %
                      </div>
                      <Tooltip
                        title={
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              Actual Labor Percentage
                            </div>
                            <div style={{ marginTop: 6 }}>
                              “Your actual labor cost compared to your actual
                              sales.”
                            </div>
                            <div style={{ marginTop: 8, fontWeight: 600 }}>
                              How is this calculated?
                            </div>
                            <div>
                              (Actual Labor Cost ÷ Actual Sales) × 100.
                            </div>
                          </div>
                        }
                      >
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: "#6B7280",
                            cursor: "default",
                          }}
                        >
                          ⓘ
                        </span>
                      </Tooltip>
                    </div>
                    <div style={primaryValueStyle}>
                      {(() => {
                        const weekActual = Number(
                          (analytics.weekTotals &&
                            analytics.weekTotals.actualLabor) ||
                            0
                        );
                        const weekForecast = Number(
                          (derivedWeekTotals &&
                            derivedWeekTotals.forecastedSales) ||
                            0
                        );
                        return weekForecast > 0
                          ? ((weekActual / weekForecast) * 100).toFixed(2) +
                              "%"
                          : "0.00%";
                      })()}
                    </div>
                  </div>
                </TableCell>
                {weekDays.map((d, i) => {
                  const key = d.toISOString().split("T")[0];
                  const perDayForecast =
                    forecastedSalesInput &&
                    typeof forecastedSalesInput[key] !== "undefined" &&
                    forecastedSalesInput[key] !== ""
                      ? Number(forecastedSalesInput[key])
                      : salesProjectionsInput &&
                        typeof salesProjectionsInput[key] !== "undefined"
                      ? Number(salesProjectionsInput[key])
                      : 0;
                  const actual =
                    analytics.actualLabor &&
                    typeof analytics.actualLabor[key] !== "undefined"
                      ? Number(analytics.actualLabor[key])
                      : 0;
                  const pct =
                    perDayForecast > 0
                      ? (actual / perDayForecast * 100).toFixed(2)
                      : "0.00";
                  const cellBg = i % 2 === 0 ? rowBg : colAltBg;
                  return (
                    <TableCell
                      key={key}
                      align="center"
                      sx={{ backgroundColor: cellBg, ...numericCellSx }}
                    >
                      {pct}%
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {/* Scheduled Productivity */}
            {(salesView === "forecast" || salesView === "both") && (
              <TableRow
                sx={{ backgroundColor: rowBg, "& td, & th": { color: "#111827" } }}
              >
                <TableCell
                  sx={{
                    ...leftBodySx,
                    color: "#111827",
                    boxShadow: "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{ ...headerStyle, maxWidth: labelMaxWidth }}
                      >
                        Sched. Prod.
                      </div>
                      <Tooltip
                        title={
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              Scheduled Productivity
                            </div>
                            <div style={{ marginTop: 6 }}>
                              “How much sales you expect to generate for every
                              scheduled labor hour.”
                            </div>
                            <div style={{ marginTop: 8, fontWeight: 600 }}>
                              How is this calculated?
                            </div>
                            <div>
                              Projected Sales ÷ Scheduled Labor Hours.
                            </div>
                          </div>
                        }
                      >
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: "#6B7280",
                            cursor: "default",
                          }}
                        >
                          ⓘ
                        </span>
                      </Tooltip>
                    </div>
                    <div style={primaryValueStyle}>
                      {(() => {
                        const weekForecast = Number(
                          (derivedWeekTotals &&
                            derivedWeekTotals.forecastedSales) ||
                            0
                        );
                        const weekHours = Number(
                          (analytics.weekTotals &&
                            analytics.weekTotals.hours) ||
                            0
                        );
                        return weekHours > 0
                          ? (weekForecast / weekHours).toLocaleString(
                              "en-US",
                              {
                                style: "currency",
                                currency: "USD",
                              }
                            )
                          : "$0.00";
                      })()}
                    </div>
                  </div>
                </TableCell>
                {weekDays.map((d, i) => {
                  const key = d.toISOString().split("T")[0];
                  const perDayForecast =
                    forecastedSalesInput &&
                    typeof forecastedSalesInput[key] !== "undefined" &&
                    forecastedSalesInput[key] !== ""
                      ? Number(forecastedSalesInput[key])
                      : salesProjectionsInput &&
                        typeof salesProjectionsInput[key] !== "undefined"
                      ? Number(salesProjectionsInput[key])
                      : 0;
                  const schedHours =
                    analytics.totalHours &&
                    typeof analytics.totalHours[key] !== "undefined"
                      ? Number(analytics.totalHours[key])
                      : 0;
                  const val =
                    schedHours > 0 ? perDayForecast / schedHours : 0;
                  return (
                    <TableCell
                      key={key}
                      align="center"
                      sx={{
                        backgroundColor: i % 2 === 0 ? rowBg : colAltBg,
                        ...numericCellSx,
                      }}
                    >
                      {schedHours > 0
                        ? val.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })
                        : "$0.00"}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {/* Actual Productivity */}
            {(salesView === "actual" || salesView === "both") && (
              <TableRow
                sx={{ backgroundColor: rowBg, "& td, & th": { color: "#111827" } }}
              >
                <TableCell
                  sx={{
                    ...leftBodySx,
                    color: "#111827",
                    boxShadow: "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{ ...headerStyle, maxWidth: labelMaxWidth }}
                      >
                        Actual Prod.
                      </div>
                      <Tooltip
                        title={
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              Actual Productivity
                            </div>
                            <div style={{ marginTop: 6 }}>
                              “How much sales your location actually generated
                              for every hour worked.”
                            </div>
                            <div style={{ marginTop: 8, fontWeight: 600 }}>
                              How is this calculated?
                            </div>
                            <div>Actual Sales ÷ Actual Labor Hours.</div>
                          </div>
                        }
                      >
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: "#6B7280",
                            cursor: "default",
                          }}
                        >
                          ⓘ
                        </span>
                      </Tooltip>
                    </div>
                    <div style={primaryValueStyle}>
                      {(() => {
                        const weekActualSales = Number(
                          (derivedWeekTotals &&
                            derivedWeekTotals.actualSales) ||
                            0
                        );
                        const weekActualHours = Number(
                          (analytics.weekTotals &&
                            analytics.weekTotals.actualHours) ||
                            0
                        );
                        return weekActualHours > 0
                          ? (weekActualSales / weekActualHours).toLocaleString(
                              "en-US",
                              {
                                style: "currency",
                                currency: "USD",
                              }
                            )
                          : "$0.00";
                      })()}
                    </div>
                  </div>
                </TableCell>
                {weekDays.map((d, i) => {
                  const key = d.toISOString().split("T")[0];
                  const perDayActual =
                    actualSalesInput &&
                    typeof actualSalesInput[key] !== "undefined" &&
                    actualSalesInput[key] !== ""
                      ? Number(actualSalesInput[key])
                      : analytics.weekTotals &&
                        analytics.weekTotals.actualSales &&
                        analytics.weekTotals.actualSalesByDay
                      ? Number(
                          analytics.weekTotals.actualSalesByDay?.[key] || 0
                        )
                      : 0;
                  const actualHrs =
                    analytics.actualHours &&
                    typeof analytics.actualHours[key] !== "undefined"
                      ? Number(analytics.actualHours[key])
                      : 0;
                  const val =
                    actualHrs > 0 ? perDayActual / actualHrs : 0;
                  return (
                    <TableCell
                      key={key}
                      align="center"
                      sx={{
                        backgroundColor: i % 2 === 0 ? rowBg : colAltBg,
                        ...numericCellSx,
                      }}
                    >
                      {actualHrs > 0
                        ? val.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })
                        : "$0.00"}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {/* Dept % rows */}
            {Object.keys(byDept)
              .filter((d) => {
                const name = (deptNameMap && deptNameMap[d]) || d || "";
                return !(
                  name === "BOH - Server" ||
                  name === "BOH - Expo" ||
                  d === "BOH - Server" ||
                  d === "BOH - Expo"
                );
              })
              .map((dept, idx) => (
                <TableRow
                  key={dept}
                  sx={{
                    backgroundColor: idx % 2 === 0 ? rowBg : rowAltBg,
                    "& td, & th": { color: "#111827" },
                  }}
                >
                  <TableCell
                    sx={{
                      ...leftBodySx,
                      color: "#111827",
                      boxShadow: "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: labelMaxWidth,
                        }}
                      >
                        {deptNameMap && deptNameMap[dept]
                          ? deptNameMap[dept]
                          : dept}{" "}
                        %
                      </div>
                      <div style={{ fontSize: 12, color: "#111827" }}>
                        {(analytics.weekTotals.pct / 7).toFixed(2)}%
                      </div>
                    </div>
                  </TableCell>
                  {weekDays.map((d) => {
                    const key = d.toISOString().split("T")[0];
                    return (
                      <TableCell key={key} align="center">
                        {analytics.deptPct[dept][key]?.toFixed(2) ?? "0.00"}%
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
